import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardBody,
  Chip,
  CircularProgress,
  Image,
} from '@nextui-org/react'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import countryObject from 'src/data/country_object.json'
import api from 'src/lib/api'
import { LanguageSelector } from 'src/components/i18n-selector'

interface SharedTender {
  uuid: string
  title: string
  buyer: string
  score: number
  totalValue: number
  valueCurrency: string
  source: string
  country: string
  publicationDate: string
  deadline: string
  publicationNumber: string
  url: string
}

interface SharedResults {
  formId: string
  tenders: SharedTender[]
  totalTenders: number
  companyName: string
  websiteProfile?: any
  createdAt: string
}

const TenderFinderShared = () => {
  const { formId } = useParams<{ formId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('tender-matching')
  const [sharedResults, setSharedResults] = useState<SharedResults | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (formId) {
      loadSharedResults(formId)
    }
  }, [formId])

  const loadSharedResults = async (id: string) => {
    try {
      setIsLoading(true)
      const response = await api.get(`/tenders/shared/${id}`)
      
      if (response.data.success) {
        setSharedResults(response.data.results)
      } else {
        setError('Results not found or expired')
      }
    } catch (error) {
      console.error('Error loading shared results:', error)
      setError('Failed to load results. The link may be invalid or expired.')
    } finally {
      setIsLoading(false)
    }
  }

  const formatValue = (tender: SharedTender) => {
    if (!tender.totalValue) return '-'
    return Intl.NumberFormat('de-DE', {
      currency: tender.valueCurrency,
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(tender.totalValue)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="mb-6 flex justify-end">
            <LanguageSelector />
          </div>
          <Card className="p-8">
            <CardBody className="text-center">
              <CircularProgress size="lg" className="mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Results...</h2>
              <p className="text-gray-600">Please wait while we load the tender opportunities.</p>
            </CardBody>
          </Card>
        </div>
      </div>
    )
  }

  if (error || !sharedResults) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="mb-6 flex justify-end">
            <LanguageSelector />
          </div>
          <Card className="p-8">
            <CardBody className="text-center">
              <Icon icon="lucide:alert-circle" className="text-red-500 text-4xl mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Results Not Found</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button
                color="primary"
                onPress={() => navigate('/')}
                startContent={<Icon icon="lucide:home" />}
              >
                Go to Homepage
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Language selector */}
        <div className="mb-6 flex justify-end">
          <LanguageSelector />
        </div>

        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Tender Opportunities</h1>
            <p className="text-gray-600">Shared tender opportunities for {sharedResults.companyName}</p>
            <div className="mt-4 text-sm text-gray-500">
              Found {sharedResults.totalTenders} total opportunities
            </div>
          </div>

          {/* Top 3 tenders */}
          <div className="space-y-4">
            {sharedResults.tenders.map((tender, index) => (
              <Card
                key={tender.uuid}
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                isPressable
                onPress={() => window.open(tender.url, '_blank')}
              >
                <CardBody>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-500">#{tender.publicationNumber}</span>
                        <Chip color="secondary" variant="flat" size="sm">
                          {tender.source}
                        </Chip>
                        <Chip color="primary" variant="flat" size="sm">
                          #{index + 1} Match
                        </Chip>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{tender.title}</h3>
                      <p className="text-gray-600 mb-2">
                        Buyer: {tender.buyer}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>
                          {(countryObject as Record<string, string>)?.[tender.country?.toUpperCase()] || tender.country}
                        </span>
                        <span>
                          Published: {dayjs(tender.publicationDate).format('MMM DD, YYYY')}
                        </span>
                        {tender.deadline && (
                          <span>
                            Deadline: {dayjs(tender.deadline).format('MMM DD, YYYY')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <CircularProgress
                        color={tender.score >= 80 ? 'secondary' : tender.score >= 60 ? 'warning' : 'success'}
                        showValueLabel={true}
                        size="lg"
                        value={tender.score}
                        className="mb-2"
                      />
                      <span className="text-lg font-semibold text-gray-900">{formatValue(tender)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">Click to view full details</div>
                    <Button
                      color="secondary"
                      variant="flat"
                      size="sm"
                      onPress={() => window.open(tender.url, '_blank')}
                      startContent={<Icon icon="lucide:external-link" />}
                    >
                      View Details
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          {/* Signup Section */}
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-purple-50">
            <CardBody className="text-center">
              <Icon icon="lucide:sparkles" className="text-blue-500 text-4xl mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Want to see all {sharedResults.totalTenders} opportunities?
              </h2>
              <p className="text-gray-600 mb-6">
                Sign up to access the complete database of tender opportunities and get personalized matches.
              </p>
              <div className="space-y-3">
                <Button
                  color="primary"
                  size="lg"
                  onPress={() => navigate('/signup')}
                  startContent={<Icon icon="lucide:arrow-right" />}
                  className="px-8 py-3"
                >
                  Sign Up for Full Access
                </Button>
                <Button
                  variant="bordered"
                  size="lg"
                  onPress={() => navigate('/')}
                  startContent={<Icon icon="lucide:home" />}
                  className="px-8 py-3"
                >
                  Back to Homepage
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default TenderFinderShared
